import { createI18n } from "vue-i18n";
import axios from "axios";
import selections from "../locale/selections-page.json";
import aside from "../locale/aside.json";
import financial from "../locale/financial.json";
import carbon from "../locale/carbon-footprint.json";
import rating from "../locale/rating.json";
import recommendations from "../locale/recommendations-page.json";
import tour from "../locale/tour.json";
import costsAndSavings from "../locale/costs-and-savings.json";
import _ from 'lodash';

const _messages = {
    en: {
      ...selections,
      ...aside,
      ...financial,
      ...carbon,
      ...rating,
      ...recommendations,
      ...tour,
      ...costsAndSavings
  }
};
const _i18n = createI18n({
    locale: 'en',
    messages: _messages,
});

export function getI18n() {
    return _i18n;
}

export async function setLocaleOverwrites(url: string, lang = 'en') {
    if (url.trim().toLowerCase() === 'none') {
        _i18n.global.setLocaleMessage(lang, ({} as any));
    } else {
        const overwrites = await axios.get(url);
        if (overwrites.status === 200 || overwrites.status === 304) {
            _i18n.global.setLocaleMessage(lang, _.merge((_messages as any)[lang], overwrites.data))
        } else {
            console.error("Error obtaining external locale file")
        }
    }
}

export function t(message: string, replace?: any) {
    return _i18n.global.t(message, replace);
}

const allowedHtmlTags = new Set([
    "a",
    "b",
    "br",
    "circle",
    "clippath",
    "defs",
    "div",
    "ellipse",
    "em",
    "g",
    "i",
    "line",
    "lineargradient",
    "li",
    "ol",
    "p",
    "path",
    "polygon",
    "polyline",
    "radialgradient",
    "rect",
    "small",
    "span",
    "stop",
    "strong",
    "svg",
    "symbol",
    "title",
    "ul",
    "use",
]);

const removeWithContentTags = new Set([
    "iframe",
    "object",
    "script",
    "style",
    "template",
]);

const allowedHtmlAttributes = new Set([
    "aria-label",
    "clip-path",
    "clip-rule",
    "class",
    "color",
    "cx",
    "cy",
    "d",
    "fill",
    "fill-color",
    "fill-opacity",
    "fill-rule",
    "focusable",
    "gradienttransform",
    "gradientunits",
    "height",
    "href",
    "id",
    "offset",
    "opacity",
    "points",
    "preserveaspectratio",
    "r",
    "rel",
    "role",
    "rx",
    "ry",
    "style",
    "stroke",
    "stroke-dasharray",
    "stroke-dashoffset",
    "stroke-linecap",
    "stroke-linejoin",
    "stroke-miterlimit",
    "stroke-opacity",
    "stroke-width",
    "target",
    "transform",
    "viewbox",
    "width",
    "x",
    "x1",
    "x2",
    "xml:space",
    "xmlns",
    "xmlns:xlink",
    "xlink:href",
    "y",
    "y1",
    "y2",
]);

const urlAttributes = new Set(["href", "xlink:href"]);
const allowedUrlProtocols = new Set(["http:", "https:", "mailto:", "tel:"]);

const allowedStyleProperties = new Set([
    "background-color",
    "border",
    "border-bottom",
    "border-color",
    "border-left",
    "border-radius",
    "border-right",
    "border-top",
    "color",
    "display",
    "font-family",
    "font-size",
    "font-style",
    "font-weight",
    "height",
    "line-height",
    "list-style-type",
    "margin",
    "margin-bottom",
    "margin-left",
    "margin-right",
    "margin-top",
    "max-height",
    "max-width",
    "min-height",
    "min-width",
    "padding",
    "padding-bottom",
    "padding-left",
    "padding-right",
    "padding-top",
    "text-align",
    "text-decoration",
    "vertical-align",
    "width",
]);

const unsafeStyleValuePattern = /(?:url\s*\(|expression\s*\(|javascript:|@import|-moz-binding)/i;

function escapeHtml(value: string): string {
    return value
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
}

function isSafeUrl(value: string): boolean {
    const trimmed = value.trim();

    if (!trimmed || trimmed.startsWith("#") || trimmed.startsWith("/") || trimmed.startsWith("./") || trimmed.startsWith("../")) {
        return true;
    }

    try {
        return allowedUrlProtocols.has(new URL(trimmed, window.location.origin).protocol);
    } catch {
        return false;
    }
}

function sanitiseStyle(value: string): string {
    return value
        .split(";")
        .map((declaration) => declaration.trim())
        .filter(Boolean)
        .map((declaration) => {
            const separatorIndex = declaration.indexOf(":");
            if (separatorIndex === -1) return "";

            const property = declaration.slice(0, separatorIndex).trim().toLowerCase();
            const propertyValue = declaration.slice(separatorIndex + 1).trim();

            if (!allowedStyleProperties.has(property) || unsafeStyleValuePattern.test(propertyValue)) {
                return "";
            }

            return `${property}: ${propertyValue}`;
        })
        .filter(Boolean)
        .join("; ");
}

function unwrapElement(element: Element): void {
    const parent = element.parentNode;
    if (!parent) return;

    while (element.firstChild) {
        parent.insertBefore(element.firstChild, element);
    }

    element.remove();
}

function sanitiseElement(element: Element): void {
    const tagName = element.tagName.toLowerCase();

    if (removeWithContentTags.has(tagName)) {
        element.remove();
        return;
    }

    if (!allowedHtmlTags.has(tagName)) {
        unwrapElement(element);
        return;
    }

    Array.from(element.attributes).forEach((attribute) => {
        const attributeName = attribute.name.toLowerCase();

        if (attributeName.startsWith("on") || !allowedHtmlAttributes.has(attributeName)) {
            element.removeAttribute(attribute.name);
            return;
        }

        if (attributeName === "style") {
            const sanitisedStyle = sanitiseStyle(attribute.value);
            if (sanitisedStyle) {
                element.setAttribute("style", sanitisedStyle);
            } else {
                element.removeAttribute(attribute.name);
            }
            return;
        }

        if (urlAttributes.has(attributeName) && !isSafeUrl(attribute.value)) {
            element.removeAttribute(attribute.name);
        }
    });

    if (tagName === "a" && element.getAttribute("target") === "_blank") {
        element.setAttribute("rel", "noopener noreferrer");
    }
}

export function safeHtml(value: unknown): string {
    const html = String(value ?? "");

    if (typeof document === "undefined") {
        return escapeHtml(html);
    }

    const template = document.createElement("template");
    template.innerHTML = html;

    Array.from(template.content.querySelectorAll("*")).forEach(sanitiseElement);

    return template.innerHTML;
}

